import React, {useState, lazy, useEffect, useLayoutEffect, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import './assets/css/index.css';
import './components/ErrorBoundary/ErrorBoundary.css';
// PERFORMANCE: Lazy load heavy components to reduce initial bundle
const LazyNavbar = lazy(() => import('./components/layout/Navbar'));
const LazyFooter = lazy(() => import('./components/layout/Footer/Footer'));
const LazyNewHome = lazy(() => import('./components/FrontPage/NewHome/NewHome'));

// Keep these for critical app functionality
import LoadingCircle from './components/ui/LoadingCircle';
import { AuthModalProvider } from './components/Login/AuthModal/AuthModalContext';
import { AuthProvider } from './contexts/AuthContext';
import { PresenceProvider } from './contexts/PresenceContext';
import { CacheProvider } from './contexts/CacheContext';
import { SearchProvider } from './contexts/SearchContext';
import { LazyFirebaseMessagingProvider } from './components/providers/LazyFirebaseMessagingProvider';
import { useAuth, useFCM } from './hooks';
import { useInitialNavigationRedirect } from './hooks/useInitialNavigationRedirect';
import OfflinePage from './components/Routing/OfflinePage';



const LoadingIndicator = () => {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <LoadingCircle />
    </div>
  );
};
const NoAcces = lazy(() => import('./components/Routing/NoAcces'));
const Fullpage = lazy(() => import('./components/typeform/FullPage'));
const Chat = lazy(() => import('./components/Chat/Chat'));
const NotFound = lazy(() => import('./components/Routing/Notfound'));
const UserProfile = lazy(() => import('./components/users/UserProfile'));
const ExpertProfile = lazy(() => import('./components/users/ExpertProfile/index'));
const UserPage = lazy(() => import('./components/users/UserPage/index'));
const ExpertPage = lazy(() => import('./components/users/ExpertPage/index'));
const UserSettings = lazy(() => import('./components/users/UserSettings'));
const ExpertSettings = lazy(() => import('./components/users/ExpertSettings'));
// === STANDARDIZED LAZY IMPORTS ===
const LazyPricing = lazy(() => import('./components/pages/TioBusiness/PricingSection'));
const LazyBlogSection = lazy(() => import('./components/pages/blogs/BlogSection'));
const LazyBlogPost = lazy(() => import('./components/pages/blogs/BlogPost'));
const LazyExpertResourcesList = lazy(() => import('./components/pages/expert-resources/ExpertResourcesList'));
const LazyExpertResourcesPost = lazy(() => import('./components/pages/expert-resources/ExpertResourcesPost'));
const LazyBecomeAnExpert = lazy(() => import('./components/frontpageexperts'));
const LazyChatInterface = lazy(() => import('./components/ChatBot/ChatInterface/ChatInterface'));
const LazyAbout = lazy(() => import('./components/pages/TioBusiness/AboutUs'));
const LazyPress = lazy(() => import('./components/pages/TioBusiness/Press'));
const LazyWhyTio = lazy(() => import('./components/FrontPage/WhyTio/WhyTio'));
const LazyHowItWorks = lazy(() => import('./components/pages/TioBusiness/HowItWorks'));
const LazyOurTeam = lazy(() => import('./components/pages/TioBusiness/OurTeam'));
const LazyOurMission = lazy(() => import('./components/pages/TioBusiness/OurMission'));
const LazyInvestors = lazy(() => import('./components/pages/TioBusiness/Investors'));
const LazyCareers = lazy(() => import('./components/pages/TioBusiness/Careers'));
const LazyPressAndMedia = lazy(() => import('./components/media/PressAndMedia'));
const LazyFAQ = lazy(() => import('./components/pages/TioBusiness/FAQ'));
const LazyContact = lazy(() => import('./components/pages/TioBusiness/Contact'));
const LazyPrivacyPolicy = lazy(() => import('./components/pages/TioBusiness/PrivacyPolicy'));
const LazyTermsOfService = lazy(() => import('./components/pages/TioBusiness/TermsOfService'));
const LazyTrustSafety = lazy(() => import('./components/pages/TioBusiness/TrustSafety'));
const LazyExpertStandards = lazy(() => import('./components/pages/TioBusiness/ExpertStandards'));
const LazyShop = lazy(() => import('./components/pages/TioBusiness/Shop'));
// const LazyExpertsLandingPage= lazy(() => import('./components/FrontPageExpert/ExpertsLandingPage')); // Replaced by LazyBecomeAnExpert
const LazySearchPage = lazy(() => import('./components/pages/Search'));

// === ADMIN DASHBOARD ===
const LazyAdminRoute = lazy(() => import('./components/admin/AdminRoute'));
const LazyAdminDashboard = lazy(() => import('./components/admin/AdminDashboard'));
const LazyMigrationPage = lazy(() => import('./pages/admin/migration'));

// === ROUTING COMPONENTS ===
const NavigationRouter = lazy(() => import('./components/routing/NavigationRouter'));

const LoadingFallback = ({ height = '100vh' }) => (
  <div style={{ minHeight: height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <LoadingCircle />
  </div>
);

const rootElement = document.getElementById('root');
const root = createRoot(rootElement);

function PrivateRoute({ children }) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingIndicator />;
  }

  return user ? children : <Navigate to="/no-access" />;
}


function ScrollToTopOnNavigation() {
  const { pathname } = useLocation();

  // PERFORMANCE FIX: Changed from useLayoutEffect to useEffect to prevent blocking
  useEffect(() => {
    window.scrollTo(0, 0); // Scroll to the top of the page
    
    // Note: NavigationRouter now handles redirect logic with component-level refs
    // No need to set session storage flags here anymore
  }, [pathname]);

  return null;
}

function ConditionalLayout({ children }) {
  const location = useLocation();
  const isFullscreenRoute = location.pathname === '/chatbot';

  if (isFullscreenRoute) {
    // Fullscreen layout - no navbar, no main wrapper
    return children;
  }

  // Normal layout with navbar and main content wrapper
  return (
    <>
      <Suspense>
        <LazyNavbar />
      </Suspense>
      <main className="main-content">
        {children}
      </main>
      <Suspense>
        <LazyFooter />
      </Suspense>
    </>
  );
}

function AppContent() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // NAVIGATION: Handle initial navigation redirect at app level
  useInitialNavigationRedirect();

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // PERFORMANCE: Route preloading removed to reduce initial parsing overhead

  // FCM token management using centralized hook
  const { isNotificationsEnabled, error: fcmError } = useFCM();
/*
  if (!isOnline) {
    return <OfflinePage />;
  }
*/
  return (
    <AuthModalProvider>
      <ConditionalLayout>
          <Routes>
          {/* === HOME & CORE === */}
          <Route path="/" element={
            <Suspense fallback={<LoadingFallback />}>
              <Home />
            </Suspense>
          } />
          <Route path="/chatbot" element={
            <Suspense fallback={<LoadingFallback />}>
              <LazyChatInterface />
            </Suspense>
          } />

          {/* === COMPANY INFO === */}
          <Route path="/about" element={
            <Suspense fallback={<LoadingFallback />}>
              <LazyAbout />
            </Suspense>
          } />
          <Route path="/our-team" element={
            <Suspense fallback={<LoadingFallback />}>
              <LazyOurTeam />
            </Suspense>
          } />
          <Route path="/our-mission" element={
            <Suspense fallback={<LoadingFallback />}>
              <LazyOurMission />
            </Suspense>
          } />
          <Route path="/how-it-works" element={
            <Suspense fallback={<LoadingFallback />}>
              <LazyHowItWorks />
            </Suspense>
          } />

          {/* === EXPERT FEATURES === */}
          <Route path="/become-an-expert" element={
            <Suspense fallback={<LoadingFallback />}>
              <LazyBecomeAnExpert />
            </Suspense>
          } />
          <Route path="/expert-standards" element={
            <Suspense fallback={<LoadingFallback />}>
              <LazyExpertStandards />
            </Suspense>
          } />
          <Route path="/resources" element={
            <Suspense fallback={<LoadingFallback />}>
              <LazyExpertResourcesList />
            </Suspense>
          } />
          <Route path="/resources/:ExpertResourcesPostId" element={
            <Suspense fallback={<LoadingFallback />}>
              <LazyExpertResourcesPost />
            </Suspense>
          } />
          {/* Redirect old expert-resources URLs to new resources URLs */}
          <Route path="/expert-resources" element={<Navigate to="/resources" replace />} />
          <Route path="/expert-resources/*" element={<Navigate to="/resources" replace />} />

          {/* === USER FEATURES === */}
          <Route path="/search" element={
            <Suspense fallback={<LoadingFallback />}>
              <LazySearchPage />
            </Suspense>
          } />
          <Route path="/pricing" element={
            <Suspense fallback={<LoadingFallback />}>
              <LazyPricing />
            </Suspense>
          } />

          {/* === CONTENT === */}
          <Route path="/blog" element={
            <Suspense fallback={<LoadingFallback />}>
              <LazyBlogSection />
            </Suspense>
          } />
          <Route path="/blog/:blogPostId" element={
            <Suspense fallback={<LoadingFallback />}>
              <LazyBlogPost />
            </Suspense>
          } />
          <Route path="/faq" element={
            <Suspense fallback={<LoadingFallback />}>
              <LazyFAQ />
            </Suspense>
          } />
          <Route path="/help" element={
            <Suspense fallback={<LoadingFallback />}>
              <LazyFAQ />
            </Suspense>
          } />
          <Route path="/help-center" element={
            <Suspense fallback={<LoadingFallback />}>
              <LazyFAQ />
            </Suspense>
          } />

          {/* === BUSINESS === */}
          <Route path="/press" element={
            <Suspense fallback={<LoadingFallback />}>
              <LazyPress />
            </Suspense>
          } />
          <Route path="/press-and-media" element={
            <Suspense fallback={<LoadingFallback />}>
              <LazyPressAndMedia />
            </Suspense>
          } />
          <Route path="/investor" element={
            <Suspense fallback={<LoadingFallback />}>
              <LazyInvestors />
            </Suspense>
          } />
          <Route path="/join-us" element={
            <Suspense fallback={<LoadingFallback />}>
              <LazyCareers />
            </Suspense>
          } />
          <Route path="/contact" element={
            <Suspense fallback={<LoadingFallback />}>
              <LazyContact />
            </Suspense>
          } />
          <Route path="/shop" element={
            <Suspense fallback={<LoadingFallback />}>
              <LazyShop />
            </Suspense>
          } />

          {/* === LEGAL === */}
          <Route path="/privacy-policy" element={
            <Suspense fallback={<LoadingFallback />}>
              <LazyPrivacyPolicy />
            </Suspense>
          } />
          <Route path="/terms-of-service" element={
            <Suspense fallback={<LoadingFallback />}>
              <LazyTermsOfService />
            </Suspense>
          } />
          <Route path="/trust-safety" element={
            <Suspense fallback={<LoadingFallback />}>
              <LazyTrustSafety />
            </Suspense>
          } />



<Route path="/user/:customURL" element={
  <Suspense>
    <UserPage />
  </Suspense>
} />

<Route path="/expert/:customURL" element={
  <Suspense>
    <ExpertPage />
  </Suspense>
} />


            <Route path="/no-access" element={<NoAcces />} />
            <Route
              path="/profile"
              element={
                <PrivateRoute>
                  <React.Suspense>
                    <UserProfile />
                  </React.Suspense>
                </PrivateRoute>
              }
            />
              <Route
              path="/expertprofile"
              element={
                <PrivateRoute>
                  <React.Suspense>
                    <ExpertProfile />
                  </React.Suspense>
                </PrivateRoute>
              }
            />
                <Route
              path="/chat"
              element={
                <PrivateRoute>
                  <React.Suspense fallback={<LoadingFallback />}>
                    <Chat />
                  </React.Suspense>
                </PrivateRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <PrivateRoute>
                  <React.Suspense fallback={<LoadingFallback />}>
                    <UserSettings />
                  </React.Suspense>
                </PrivateRoute>
              }
            />
             <Route
              path="/expertsettings"
              element={
                <PrivateRoute>
                  <React.Suspense fallback={<LoadingFallback />}>
                    <ExpertSettings />
                  </React.Suspense>
                </PrivateRoute>
              }
            />
            <Route
              path="/ExpertVerification"
              element={
                <PrivateRoute>
                  <React.Suspense fallback={<LoadingFallback />}>
                    <Fullpage />
                  </React.Suspense>
                </PrivateRoute>
              }
            />

            {/* === MIGRATION === */}
            <Route 
              path="/admin/migration" 
              element={
                <React.Suspense fallback={<LoadingFallback />}>
                  <LazyMigrationPage />
                </React.Suspense>
              } 
            />

            {/* === ADMIN DASHBOARD === */}
            <Route 
              path="/admin/*" 
              element={
                <PrivateRoute>
                  <React.Suspense fallback={<LoadingFallback />}>
                    <LazyAdminRoute>
                      <LazyAdminDashboard />
                    </LazyAdminRoute>
                  </React.Suspense>
                </PrivateRoute>
              } 
            />

            <Route path="*" element={<NotFound />} />
            </Routes>
          </ConditionalLayout>
          <ScrollToTopOnNavigation />
      </AuthModalProvider>
  );
}

function App() {
  return (
    <BrowserRouter basename={process.env.PUBLIC_URL}>
      <AuthProvider>
        <PresenceProvider>
          <CacheProvider>
            <SearchProvider>
              <LazyFirebaseMessagingProvider>
                <AppContent />
              </LazyFirebaseMessagingProvider>
            </SearchProvider>
          </CacheProvider>
        </PresenceProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}


// === HOME PAGE COMPONENTS ===
const LazyHomeFAQ = lazy(() => import('./components/FrontPage/FAQ/FAQ'));
const LazyFloatingExperts = lazy(() => import('./components/FrontPage/FloatingExperts/FloatingExperts'));
const LazyTioCapabilities = lazy(() => import('./components/FrontPage/TioCapabilities/TioCapabilities'));
const LazyWeHelp = lazy(() => import('./components/FrontPage/WeHelp/WeHelp'));
const LazyCategoryIcons = lazy(() => import('./components/FrontPage/CategoryIcons/CategoryIcons'));
const LazyProblemSolutionBridge = lazy(() => import('./components/FrontPage/ProblemSolutionBridge/ProblemSolutionBridge'));
const LazyUserMidPageCTA = lazy(() => import('./components/FrontPage/UserMidPageCTA/UserMidPageCTA'));
const LazyInteractiveCardStack = lazy(() => import('./components/FrontPage/InteractiveCardStack/InteractiveCardStack'));

function Home() {
  return (
    <>
      {/* PERFORMANCE: Lazy load NewHome to reduce initial bundle */}
      <Suspense fallback={<LoadingFallback height="800px" />}>
        <LazyNewHome />
        <LazyProblemSolutionBridge />
        <LazyFloatingExperts />
        <LazyTioCapabilities />
        <LazyWhyTio />
        <LazyInteractiveCardStack />
        <LazyCategoryIcons />
        <LazyWeHelp />
        <LazyHomeFAQ />
        <LazyUserMidPageCTA />
      </Suspense>
    </>
  );
}

root.render(<App />);

